import React, { useContext, useCallback } from 'react';
import type { FC } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';
import { mergeRefs } from 'use-callback-ref';

import Button from '@atlaskit/button/custom-theme-button';
import EditorAddIcon from '@atlaskit/icon/core/add';
import type { TriggerProps } from '@atlaskit/popup';
import { token } from '@atlaskit/tokens';

import { AppNavigationContext } from '@confluence/app-navigation-context';
import { useIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/useIsNav4Enabled';
import { LoadablePaint } from '@confluence/loadable';

import { usePreloadCreationMenuData } from '../usePreloadCreationMenuData';

import {
	RESPONSIVE_CONTENT_WIDTH_THRESHOLD,
	IconWrapper,
	CreateContentButtonWrapper,
} from './CreateButtonStyles';

const CreateButton = LoadablePaint<any>({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-TopNav" */ '@atlaskit/navigation-system/top-nav-items'
			)
		).CreateButton,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CreateContentButtonTextWrapper = styled.span({
	display: 'inline',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	[`@media (max-width: ${RESPONSIVE_CONTENT_WIDTH_THRESHOLD}px)`]: {
		display: 'none',
	},
});

const i18n = defineMessages({
	create: {
		id: 'create-blank-fabric-page.create.content.popover.button',
		defaultMessage: 'Create',
		description: 'button to display popover',
	},
	createContentLabel: {
		id: 'create-blank-fabric-page.create.content.aria.label',
		defaultMessage: 'Create content',
		description: 'The aria label for create button',
	},
});

export const getCreateButtonTheme = (createButtonTheme) => (current, props) => {
	const { buttonStyles, spinnerStyles } = current(props);
	return {
		buttonStyles: {
			...buttonStyles,
			...createButtonTheme.default,
			margin: 0,
			fontWeight: token('font.weight.medium'),
			':hover': createButtonTheme.hover,
			':focus': createButtonTheme.focus,
			// :active doesn't work in FF, because we do a
			// e.preventDefault() on mouse down in Button.
			// '&&' is required to add more CSS specificity
			// && it not a valid CSSObject property
			// @ts-ignore
			'&&': {
				...(props.state === 'active' && createButtonTheme.active),
			},
		},
		spinnerStyles,
	};
};

export const GlobalCreateContentButton: FC<{
	triggerProps: TriggerProps;
	onClick: () => void;
}> = ({ triggerProps, onClick }) => {
	const { ref } = triggerProps;
	const intl = useIntl();
	const { theme, createContentPopoverButtonRef } = useContext(AppNavigationContext);
	const preloadCreationMenuData = usePreloadCreationMenuData();

	const handleClick = useCallback(() => {
		onClick();
	}, [onClick]);

	const buttonTheme = theme?.mode?.create ? getCreateButtonTheme(theme.mode.create) : undefined;

	const isNav4Enabled = useIsNav4Enabled();
	const createButton = isNav4Enabled ? (
		<CreateButton onClick={handleClick}>
			<FormattedMessage {...i18n.create} />
		</CreateButton>
	) : (
		<Button
			appearance="primary"
			theme={buttonTheme}
			onClick={handleClick}
			iconBefore={
				<IconWrapper>
					<EditorAddIcon spacing="spacious" label="" data-vc="global-content-create-button" />
				</IconWrapper>
			}
			testId="create-content-popover-button"
			{...triggerProps}
			aria-label={intl.formatMessage(i18n.createContentLabel)}
			ref={
				createContentPopoverButtonRef
					? mergeRefs([triggerProps.ref, createContentPopoverButtonRef])
					: triggerProps.ref
			}
		>
			<CreateContentButtonTextWrapper>
				<FormattedMessage {...i18n.create} />
			</CreateContentButtonTextWrapper>
		</Button>
	);

	const getCreateButton = () => {
		return createButton;
	};

	return (
		<CreateContentButtonWrapper
			data-vc="create-content-button-wrapper"
			ref={ref}
			onMouseEnter={preloadCreationMenuData}
			onFocus={preloadCreationMenuData}
			isNav4Enabled={isNav4Enabled}
		>
			{getCreateButton()}
		</CreateContentButtonWrapper>
	);
};
